import React from "react";
import { useDropzone, DropzoneOptions, DropEvent } from "react-dropzone";

// Icons
import UploadIcon from "../../images/Icon_Upload.png";
import WarningIcon from "../../images/Icon_Warning.png";

type DropzoneProps = {
  children?(isDragActive: boolean): React.ReactNode;
  uploadedFile?: File;
  onDrop<T extends File>(
    acceptedFiles: T[],
    rejectedFiles: T[],
    event: DropEvent
  ): void;
  fileType?: string;
  dropzoneOptions?: DropzoneOptions;
  readOnly: boolean;
  dropzoneText?: string;
  uploadIcon?: string;
  warningIcon?: string;
  showWarningMessage?: string | null;
  showAltWarningMessage?: string | null;
  hasUploadedImage?: boolean;
  uploadedImageUrl?: string;
  fileName?: string;
  acceptedTypes?: string[] | null;
  onlyShowFileName?: boolean;
};

function Dropzone<T>(props: DropzoneProps) {
  const [imageBase64, setImageBase64] = React.useState<string>("");

  if (props.uploadedFile && props.uploadedFile.type) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageBase64 = reader.result;
      setImageBase64(imageBase64 as string);
    };
    reader.readAsDataURL(props.uploadedFile);
  }

  const dropzoneOptObj = {
    ...props.dropzoneOptions,
    onDrop: props.onDrop,
    disabled: props.readOnly,
  };
  
  if(props.acceptedTypes){
    dropzoneOptObj.accept = props.acceptedTypes;
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptObj);

  return (
    <div
      className={
        props.uploadedFile || props.hasUploadedImage
          ? "Dropzone Disabled"
          : "Dropzone"
      }
      {...getRootProps()}
    >
      {/* If no files uploaded */}
      {!props.uploadedFile && (
        <>
          <input
            {...getInputProps({
              multiple: false,
            })}
          />

          {props.children ? (
            props.children(isDragActive)
          ) : props.hasUploadedImage ? (
            <>
              <img src={props.uploadedImageUrl} alt="Upload" />
            </>
          ) : (
            <>
              {!props.showWarningMessage && !props.showAltWarningMessage ? (
                <div className="NoImgPlaceholder">
                  {props.uploadIcon ? (
                    <img className="Icon" src={props.uploadIcon} alt="Upload" />
                  ) : (
                    <img className="Icon" src={UploadIcon} alt="Upload" />
                  )}

                  {props.dropzoneText && <p>{props.dropzoneText}</p>}
                </div>
              ) : (
                <>
                  {props.showWarningMessage ? 
                    <>
                      <span className="WarningMessage NoImage">
                        {props.warningIcon ? (
                          <img
                            className="Icon"
                            src={props.warningIcon}
                            alt="Upload"
                          />
                        ) : (
                          <img className="Icon" src={WarningIcon} alt="Upload" />
                        )}

                        <div>{props.showWarningMessage}</div>
                      </span> 
                    </>
                    : props.showAltWarningMessage ? 
                      <>
                        <p className="ErrorTxt2">{props.showAltWarningMessage}</p>
                      </>
                    : <></>
                  }                 
                </>
              )}
            </>
          )}
        </>
      )}

      {/* If file uploaded, display it */}
      {props.uploadedFile && (
        <>
          {props.fileName !== "" && props.fileName !== undefined ? (
            <>
              {props.onlyShowFileName ? 
                <p>{props.fileName}</p>
                :
                <img
                  className="UploadedImage"
                  src={props.uploadedImageUrl}
                  alt={props.fileName}
                />
              }
            </>
          ) : (
            <>
              {props.fileType && props.fileType === "image" && !props.onlyShowFileName ? (
                <img
                  className="UploadedImage"
                  src={imageBase64}
                  alt={props.uploadedFile.name}
                />
              ) : (
                <div className="FileName"><p>{props.uploadedFile.name || props.fileName}</p></div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export { Dropzone };
